// Service de gestion des notifications push
import { messaging } from './firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

// Clé VAPID (à générer depuis Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = 'BNPD34zOXwB2ywHMg6U0h8FvnV0dqV5dvsGH_Fq_vxWEHM4PlMTgcTSvZBW0OrY9wabPzezieKSLaEWvObvnVi0';

/**
 * Demander la permission pour les notifications push
 * @param {string} userId - ID de l'utilisateur Firebase
 * @param {string} companyId - ID de l'entreprise
 * @returns {Promise<boolean>} true si la permission est accordée
 */
export async function requestNotificationPermission(userId, companyId) {
  // Vérifier le support de base
  if (!areNotificationsSupported()) {
    console.error('❌ Notifications non supportées sur cet appareil/navigateur');
    return false;
  }

  // Vérifier Firebase Messaging (peut être null sur certains navigateurs)
  if (!messaging) {
    console.error('❌ Firebase Messaging non initialisé');
    console.info('Vérifiez : HTTPS activé, Service Worker enregistré, Navigateur compatible');
    return false;
  }

  try {
    // Demander la permission
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      console.log('✅ Permission de notification accordée');
      
      // Récupérer le token FCM
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY
      });
      
      if (token) {
        console.log('📱 Token FCM obtenu:', token.substring(0, 20) + '...');
        
        // Sauvegarder le token dans Firestore
        await saveNotificationToken(userId, companyId, token);
        
        return true;
      } else {
        console.warn('⚠️ Impossible d\'obtenir le token FCM');
        return false;
      }
    } else if (permission === 'denied') {
      console.warn('❌ Permission de notification refusée');
      return false;
    } else {
      console.log('⏸️ Permission de notification en attente');
      return false;
    }
  } catch (error) {
    console.error('Erreur demande permission notifications:', error);
    return false;
  }
}

/**
 * Sauvegarder le token FCM dans Firestore
 */
async function saveNotificationToken(userId, companyId, token) {
  try {
    const tokenRef = doc(db, 'users', userId);
    
    // Récupérer les tokens existants
    const userDoc = await getDoc(tokenRef);
    const userData = userDoc.data() || {};
    const existingTokens = userData.fcmTokens || [];
    
    // Ajouter le nouveau token s'il n'existe pas déjà
    if (!existingTokens.includes(token)) {
      existingTokens.push(token);
      
      await setDoc(tokenRef, {
        fcmTokens: existingTokens,
        lastTokenUpdate: new Date(),
        companyId: companyId
      }, { merge: true });
      
      console.log('💾 Token FCM sauvegardé');
    }
  } catch (error) {
    console.error('Erreur sauvegarde token FCM:', error);
  }
}

/**
 * Écouter les notifications en foreground (quand l'app est ouverte)
 * @param {Function} callback - Fonction appelée quand une notification arrive
 */
export function onForegroundMessage(callback) {
  if (!messaging) return;
  
  onMessage(messaging, (payload) => {
    console.log('📬 Notification reçue (foreground):', payload);
    
    // Afficher une notification personnalisée
    if (payload.notification) {
      const { title, body, icon } = payload.notification;
      
      // Créer une notification native
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(title, {
          body: body,
          icon: icon || '/logo192.png',
          badge: '/logo192.png',
          tag: payload.data?.type || 'default',
          requireInteraction: false,
          data: payload.data
        });
      }
      
      // Appeler le callback avec les données
      if (callback) {
        callback(payload);
      }
    }
  });
}

/**
 * Détection iOS
 */
function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/**
 * Détection Samsung Internet Browser
 */
function isSamsungBrowser() {
  return /SamsungBrowser/i.test(navigator.userAgent);
}

/**
 * Détection si l'app est en mode standalone (ajoutée à l'écran d'accueil)
 */
function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
}

/**
 * Vérifier si les notifications sont supportées et activées
 */
export function areNotificationsSupported() {
  // Vérifications de base d'abord
  const hasNotificationAPI = 'Notification' in window;
  const hasServiceWorker = 'serviceWorker' in navigator;
  
  // Sur Firebase Hosting, on est TOUJOURS en HTTPS, donc forcer true
  const isForcedSecure = window.location.hostname.includes('firebaseapp.com') || 
                         window.location.hostname.includes('web.app') ||
                         window.location.hostname === 'localhost';
  
  const isSecureContext = isForcedSecure || window.isSecureContext || window.location.protocol === 'https:';
  
  // Samsung Internet Browser : vérifications spéciales
  if (isSamsungBrowser()) {
    console.log('📱 Samsung Internet Browser détecté');
    
    // Samsung Internet supporte les notifications MAIS nécessite parfois d'ajouter à l'écran d'accueil
    // On va quand même permettre d'essayer sans être en standalone
    if (!hasNotificationAPI) {
      console.warn('❌ API Notification non disponible sur Samsung Internet');
      return false;
    }
    
    if (!hasServiceWorker) {
      console.warn('❌ Service Worker non supporté sur cette version de Samsung Internet');
      return false;
    }
    
    if (!isSecureContext) {
      console.warn('❌ HTTPS requis');
      return false;
    }
    
    // Samsung Internet supporte les notifications, on retourne true
    console.log('✅ Samsung Internet : Notifications supportées');
    return true;
  }
  
  // Sur iOS, vérifier des conditions spéciales
  if (isIOSDevice()) {
    console.log('📱 Appareil iOS détecté');
    
    // iOS nécessite que l'app soit ajoutée à l'écran d'accueil
    if (!isStandalone()) {
      console.warn('⚠️ Sur iOS, vous devez ajouter l\'app à l\'écran d\'accueil pour les notifications');
      return false;
    }
    
    // Vérifier iOS 16.4+
    const match = navigator.userAgent.match(/OS (\d+)_(\d+)/);
    if (match) {
      const majorVersion = parseInt(match[1], 10);
      const minorVersion = parseInt(match[2], 10);
      if (majorVersion < 16 || (majorVersion === 16 && minorVersion < 4)) {
        console.warn('⚠️ iOS 16.4 ou supérieur requis pour les notifications');
        return false;
      }
    }
  }
  
  // Debug détaillé pour autres navigateurs
  if (!hasNotificationAPI) {
    console.warn('❌ API Notification non disponible');
    return false;
  }
  
  if (!hasServiceWorker) {
    console.warn('❌ Service Worker non supporté');
    return false;
  }
  
  if (!isSecureContext) {
    console.warn('❌ Contexte non sécurisé (HTTPS requis)');
    return false;
  }
  
  console.log('✅ Notifications supportées');
  return true;
}

/**
 * Obtenir les informations de support détaillées pour l'UI
 */
export function getNotificationSupportInfo() {
  const iOS = isIOSDevice();
  const samsung = isSamsungBrowser();
  const standalone = isStandalone();
  
  // Forcer HTTPS sur Firebase Hosting
  const isForcedSecure = window.location.hostname.includes('firebaseapp.com') || 
                         window.location.hostname.includes('web.app') ||
                         window.location.hostname === 'localhost';
  
  return {
    isIOS: iOS,
    isSamsung: samsung,
    isStandalone: standalone,
    needsStandalone: iOS && !standalone,
    recommendStandalone: samsung && !standalone, // Samsung fonctionne mieux en standalone
    hasNotificationAPI: 'Notification' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    isSecureContext: isForcedSecure || window.isSecureContext || window.location.protocol === 'https:',
    browserName: samsung ? 'Samsung Internet' : (iOS ? 'Safari iOS' : 'Autre'),
    currentUrl: window.location.href,
    protocol: window.location.protocol
  };
}

/**
 * Obtenir le statut actuel de la permission
 */
export function getNotificationPermission() {
  if (!areNotificationsSupported()) return 'unsupported';
  return Notification.permission; // 'default', 'granted', 'denied'
}

/**
 * Désactiver les notifications pour cet appareil
 */
export async function disableNotifications(userId) {
  try {
    const tokenRef = doc(db, 'users', userId);
    await setDoc(tokenRef, {
      fcmTokens: [],
      notificationsEnabled: false
    }, { merge: true });
    
    console.log('🔕 Notifications désactivées');
    return true;
  } catch (error) {
    console.error('Erreur désactivation notifications:', error);
    return false;
  }
}
